import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const InfrastructureDevelopment = () => {
  const phrases = [
    "Cloud Native Systems",
    "Infrastructure Development"
  ];

  const cardData = [
    { type: 'purple-clay', content: '☁️' },
    { type: 'smooth-wave', content: '🌐' },
    { type: 'abstract-blocks', content: '🧱' },
    { type: 'pink-sphere', content: '🚀' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🏗️", title: "Infrastructure as Code (IaC)", desc: "Programmatically deploy and version high-availability cluster foundations using Terraform and OpenTofu." },
    { icon: "☁️", title: "Multi-Cloud Architectures", desc: "Design fault-tolerant, elastic microservice environments across AWS, Azure, and Google Cloud Platform environments." },
    { icon: "☸️", title: "Kubernetes Orchestration", desc: "Structure production-grade container clusters, service meshes, dynamic ingress patterns, and autoscaling metrics." },
    { icon: "🌐", title: "Hybrid Virtual Networking", desc: "Engineer secure software-defined networks, software gateways, internal peering pathways, and global load balancers." },
    { icon: "🔄", title: "CI/CD Pipeline Frameworks", desc: "Build automated testing, build artifact registries, and progressive canary deployment tracks using GitOps paradigms." },
    { icon: "💾", title: "Distributed Storage Systems", desc: "Configure high-throughput object, block, and relational databases engineered for sub-millisecond global availability." },
    { icon: "🛡️", title: "Zero-Trust DevSecOps Shields", desc: "Inject strict IAM boundaries, continuous policy-as-code audits, and secret management engines into production pools." }
  ];

  const advancedServices = [
    { icon: "📡", title: "Observability & Telemetry Loops", desc: "Establish deep logging, continuous tracing matrices, and real-time behavior alerts via Prometheus and Grafana arrays." },
    { icon: "⚡", title: "Serverless Compute Fabrics", desc: "Deploy event-driven execution blocks that auto-scale instantly from absolute zero to millions of daily actions." },
    { icon: "📦", title: "Immutable Base Image Design", desc: "Bake secure, minimized container bases and operating systems to lower execution overhead and exposure areas." },
    { icon: "🏢", title: "Bare-Metal Hybrid Data Links", desc: "Bridge on-premises system arrays safely with modern public cloud endpoints using dedicated fiber tunnels." },
    { icon: "📉", title: "FinOps Cost-Efficiency Scans", desc: "Conduct active resource allocation tests, isolate idle computing nodes, and configure modern cluster density configurations." },
    { icon: "🧬", title: "Disaster Recovery & Hot Syncs", desc: "Structure continuous multi-region replication layers and zero-data-loss failover strategies for core system records." },
    { icon: "🔧", title: "Configuration Management Engines", desc: "Automate large-scale operating system configuration tasks, cluster updates, and node configurations using Ansible arrays." },
    { icon: "🛣️", title: "Edge Network Content Routing", desc: "Configure intelligent global distribution networks and caching structures to terminate user requests directly at the edge." },
    { icon: "🧪", title: "Chaos Engineering Experiments", desc: "Conduct automated injection tests targeting network latency and component drops to fortify cluster resilience." }
  ];

  const technologies = {
    "Cloud Frameworks": ["Amazon Web Services", "Microsoft Azure", "Google Cloud Platform", "Hybrid OpenStack Platforms"],
    "Automation & IaC": ["Terraform / OpenTofu", "Ansible Configuration", "Crossplane Engine", "Pulumi Code Formats"],
    "Container Control": ["Kubernetes (EKS/AKS/GKE)", "Docker Containers", "Istio Service Mesh", "ArgoCD GitOps Loop"],
    "Data & Caching Blocks": ["PostgreSQL Clusters", "Redis In-Memory Tier", "Ceph Distributed Storage", "Apache Kafka Streams"],
    "Monitoring Layers": ["Prometheus Ecosystem", "Grafana Dashboards", "OpenTelemetry API", "Datadog Telemetry"]
  };

  const processSteps = [
    { num: "01", title: "Topological Analysis", desc: "Analyzing legacy workload patterns, performance bottlenecks, and resource usage margins." },
    { num: "02", title: "Blueprint Engineering", desc: "Designing multi-tier virtual networks, cluster layouts, security perimeters, and failure planes." },
    { num: "03", title: "IaC Declarative Coding", desc: "Writing clean, reusable platform scripts to configure secure target computing networks dynamically." },
    { num: "04", title: "Pipeline Integration", desc: "Binding version control pipelines to automated test triggers, code scans, and deployment systems." },
    { num: "05", title: "Hardening & Compliance", desc: "Verifying firewall routing logic, resource isolation states, encryption setups, and access rights." },
    { num: "06", title: "Workload Migration", desc: "Executing safe database syncing, DNS cutovers, and traffic redirection sequences with zero downtime." },
    { num: "07", title: "Telemetry Fine-Tuning", desc: "Reviewing performance metrics, balancing cluster capacity ratios, and optimizing auto-scaling loops." }
  ];

  const industries = [
    "High-Volume SaaS Platforms", "Global FinTech Applications", "E-Commerce & Retail Clusters", "Streaming & Media Distribution",
    "Healthcare & Life Sciences", "Logistics & Supply Chain Webs", "Telecommunications Systems", "Automotive & Connected Fleets",
    "Energy Grid Data Hubs", "Enterprise Banking Infrastructures", "AI & Machine Learning Nodes", "Critical Government Clouds"
  ];

  const designBenefits = [
    "Airtight Zero-Trust Security", "Elastic Sub-Second Autoscaling", "Immutable Infrastructure Baselines",
    "Near-Zero Recovery Time Objectives", "Dramatically Reduced Compute Waist", "Standardized Declarative Deployments",
    "Comprehensive Platform Observability", "Complete Cloud Vendor Flexibility"
  ];

  const whyChooseUs = [
    "Certified Cloud & DevOps Architects", "Declarative, Version-Controlled Systems", "Deep Container Orchestration Prowess",
    "Flawless Zero-Downtime Data Migrations", "Security-First System Hardening Stance", "High-Performance Compute Topologies",
    "Proactive Resilience Injection Frameworks", "Transparent Structural Delivery Timelines", "Proven Distributed Database Architecture",
    "Continuous Live Cluster Operations Support", "Modern Platform Engineering Principles", "100% System Availability Commitments"
  ];

  return (

    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Infrastructure Development Services | IT Infrastructure Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Infrastructure Development Services | IT Infrastructure Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides reliable infrastructure development services, including IT infrastructure setup, cloud infrastructure, network design, server management, virtualization, data center solutions, cybersecurity, and infrastructure consulting for businesses."
        />

        <meta
          name="keywords"
          content="Infrastructure Development, IT Infrastructure Services, Infrastructure Solutions, Cloud Infrastructure, Network Infrastructure, Server Management, Data Center Solutions, Infrastructure Consulting, IT Infrastructure Management, Virtualization Services, Network Security, Cloud Migration, Infrastructure Automation, Enterprise Infrastructure, IT Support Services, Managed Infrastructure Services, Infrastructure Development Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/infrastructure-development"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Infrastructure Development Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Build a secure, scalable, and high-performance IT infrastructure with Affsoft IT Solutions Pvt. Ltd. We offer cloud infrastructure, server management, network solutions, virtualization, cybersecurity, and enterprise infrastructure services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/infrastructure-development"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Infrastructure Development Services | IT Infrastructure Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Strengthen your business with expert infrastructure development services from Affsoft IT Solutions Pvt. Ltd. We deliver cloud, server, network, virtualization, and managed infrastructure solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="infrastructure-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#09111c' // dark structural fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Cloud Native Systems &nbsp; and &nbsp;</span>
              <span>Infrastructure Development </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-info fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Infrastructure Development</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we transform complex computing configurations into resilient, production-grade cloud environments. Our advanced platform frameworks deliver complete lifecycle automation, secure networking topology, and scalable cluster design to keep enterprise workloads performing at peak.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-info fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE PLATFORM SOLUTIONS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Platform Engineering</span>
              <h2 className="fw-bold text-dark display-6">Resilient System Design Built for High Availability</h2>
              <p className="text-muted mt-3">
                Manual server provisioning and loose configurations open paths to downtime and data exposure. At Affsoft IT Solutions Pvt. Ltd., we construct fully automated, declarative computing environments that optimize reliability and secure software execution maps.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Infrastructure Architecture Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Modernize Your Compute Environments?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED AUTOMATION & DEVSECOPS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase">Advanced Operations</span>
              <h2 className="fw-bold text-dark display-6">Telemetry & Cost Controls Programmed with Precision</h2>
              <p className="text-muted mt-3">
                We extend fundamental cloud setups into deep, observable execution frameworks. From active node vulnerability scanning to automated micro-scaling policies, our operations deliver stable runtime architectures built to adapt.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-info">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Platform Tech Stack</span>
              <h2 className="fw-bold display-6">Cloud Subsystems & Utility Libraries We Leverage</h2>
              <p className="text-light-50">We build and validate complex orchestration tracks using native, industry-standard tool chains.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-info mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-info opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Execution Path</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Infrastructure Lifecycle</h2>
              <p className="text-muted">A methodical milestone matrix transforming physical and raw virtual computing segments into dynamic systems.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-info border-4 h-100 shadow-sm">
                    <div className="fw-bold text-info display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We pair strict programmatic controls with multi-cloud topology models to operate applications safely.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-info fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default InfrastructureDevelopment;